import { PayloadAction, createSlice } from "@reduxjs/toolkit"
import { revertAll } from "../revertAll"
import type {
  AdSet,
  SupportedPlatform,
} from "~/ui-rtk/api/types/paid-media.types"
import type {
  AdLauncherAd,
  PlacementAssets,
  ScrapedImage,
} from "~/ui-rtk/pages/AdForge/types"

export interface PrefillCreative {
  primaryText?: string
  headline?: string
  description?: string
  destinationUrl?: string
  callToAction?: string
  adName?: string
}

/**
 * Source to clone when blasting into other ad sets. Two modes:
 *  - `type: "ad"` (default, back-compat) — clone an existing Meta/TikTok ad
 *    by id; `adId` is required.
 *  - `type: "post"` — promote an organic post via the existing-post creative
 *    path; `postId` is required, and `caption` may pre-fill the primary text.
 *
 * Set when the user enters the page from an Ad Details or Post Details
 * drawer; cleared by `resetAdForge` or `setSourceAd(undefined)`.
 */
export interface SourceAd {
  type?: "ad" | "post"
  /** Required when `type !== "post"`. */
  adId?: string
  /** Required when `type === "post"`. */
  postId?: string
  platform: SupportedPlatform
  name: string
  imageUrl?: string
  mediaChannel?: string
  /** Organic post caption — used to pre-fill primary text in post mode. */
  caption?: string
}

/**
 * Structured product facts carried in from the Mega Feed, injected into the
 * AI compose / motion prompts so the model knows what it is actually
 * advertising.
 *
 * AiActionDialog can already derive a rough digest by scraping a product URL
 * the user pastes. This is the same idea sourced from the feed instead: the
 * crawler and LLM extraction already ran, so the copy, specs and taxonomy are
 * cleaner and no scrape round-trip is needed.
 */
export interface ProductContext {
  productId?: string
  name?: string
  /** LLM one-liner — the most prompt-usable summary. */
  headerDescription?: string
  description?: string
  specs?: string
  brand?: string
  productType?: string
  googleProductCategory?: string
  gender?: string
  ageGroup?: string
  price?: string
  priceCurrency?: string
  tags?: string
  url?: string
}

export interface DestinationOverride {
  callToAction?: string
  destinationUrl?: string
  tracking?: Array<{ key: string; value: string }>
  assetIds?: string[]
  placementAssets?: PlacementAssets
  skip?: boolean
}

export interface AdForgeState {
  ads: AdLauncherAd[]
  globalAdSetIds: string[]
  phase: "build" | "review"
  /** Pool of all assets the user has gathered (modal picks, source-ad seed,
   *  uploads, library picks). Superset of `selectedAssets`. The form renders
   *  this as a container; users click items to promote them into the ad. */
  pickedAssets: ScrapedImage[]
  /** The actual ad assets — cap of 1 (single) / 10 (carousel) is enforced at
   *  the click sites that promote from the pool. */
  selectedAssets: ScrapedImage[]
  selectedAdSetIds: string[]
  /**
   * Persistent cache of AdSet metadata for every id that has ever been selected.
   * Populated when the user toggles selection, NEVER cleared by search/filter
   * state — so the builder can resolve `isProfileVisit`, `name`, `campaign`, etc.
   * for a selected ad set even when it's not in the current search result.
   */
  selectedAdSetsData: Record<string, AdSet>
  destinationOverrides: Record<string, DestinationOverride>
  prefillCreative?: PrefillCreative
  sourceAd?: SourceAd
  /** Set when the session was seeded from a Mega Feed product. */
  productContext?: ProductContext
  /**
   * One-click intent: an AdForge AI flow to open as soon as the page mounts.
   * Set by the Mega Feed's "Create ad video" action so the user lands directly
   * in Image → Video with the product's assets already pooled, instead of
   * having to pick a hero image and hunt for the flow. Cleared by the page once
   * consumed so a later manual visit doesn't re-trigger it.
   */
  pendingAiAction?: "image-to-video" | "image-to-carousel" | "slideshow"
}

const initialState: AdForgeState = {
  ads: [],
  globalAdSetIds: [],
  phase: "build",
  pickedAssets: [],
  selectedAssets: [],
  selectedAdSetIds: [],
  selectedAdSetsData: {},
  destinationOverrides: {},
}

export const adForgeSlice = createSlice({
  name: "adForge",
  initialState,
  extraReducers: builder => builder.addCase(revertAll, () => initialState),
  reducers: {
    setSelectedAssets(state, { payload }: PayloadAction<ScrapedImage[]>) {
      state.selectedAssets = payload
      // Mirror anything that lands in the ad into the pool as well.
      const inPool = new Set(state.pickedAssets.map(a => a.id))
      for (const a of payload) {
        if (!inPool.has(a.id)) state.pickedAssets.push(a)
      }
    },
    addSelectedAsset(state, { payload }: PayloadAction<ScrapedImage>) {
      // Identity is the asset id, not the perceptual hash. pHash is computed on a
      // greyscale DCT, so one product shot in three colourways hashes identically —
      // matching on it silently swallowed two of every three picks. Two rows that
      // reach the UI as separate assets are separate assets; AMS already dedups.
      const isDuplicate = (a: ScrapedImage) => a.id === payload.id
      if (!state.selectedAssets.some(isDuplicate)) {
        state.selectedAssets.push(payload)
      }
      if (!state.pickedAssets.some(isDuplicate)) {
        state.pickedAssets.push(payload)
      }
    },
    removeSelectedAsset(state, { payload }: PayloadAction<string>) {
      state.selectedAssets = state.selectedAssets.filter(a => a.id !== payload)
    },
    /**
     * Merge updates into a card/pool asset in place (e.g. a freshly picked
     * video thumbnail) without disturbing slot order or selection.
     */
    updateSelectedAssetById(
      state,
      {
        payload,
      }: PayloadAction<{ id: string; updates: Partial<ScrapedImage> }>,
    ) {
      for (const list of [state.selectedAssets, state.pickedAssets]) {
        const idx = list.findIndex(a => a.id === payload.id)
        if (idx >= 0) list[idx] = { ...list[idx], ...payload.updates }
      }
    },
    setPickedAssets(state, { payload }: PayloadAction<ScrapedImage[]>) {
      state.pickedAssets = payload
      // Drop any ad asset that no longer exists in the pool.
      const ids = new Set(payload.map(a => a.id))
      state.selectedAssets = state.selectedAssets.filter(a => ids.has(a.id))
    },
    addPickedAsset(state, { payload }: PayloadAction<ScrapedImage>) {
      // Same as addSelectedAsset: id is identity, pHash is colour-blind.
      const isDuplicate = (a: ScrapedImage) => a.id === payload.id
      if (!state.pickedAssets.some(isDuplicate)) {
        state.pickedAssets.push(payload)
      }
    },
    removePickedAsset(state, { payload }: PayloadAction<string>) {
      state.pickedAssets = state.pickedAssets.filter(a => a.id !== payload)
      state.selectedAssets = state.selectedAssets.filter(a => a.id !== payload)
    },
    addAd(state, { payload }: PayloadAction<AdLauncherAd>) {
      state.ads.push(payload)
    },
    removeAd(state, { payload }: PayloadAction<string>) {
      state.ads = state.ads.filter(ad => ad.id !== payload)
    },
    updateAd(
      state,
      {
        payload,
      }: PayloadAction<{ id: string; updates: Partial<AdLauncherAd> }>,
    ) {
      const idx = state.ads.findIndex(ad => ad.id === payload.id)
      if (idx >= 0) {
        state.ads[idx] = { ...state.ads[idx], ...payload.updates }
      }
    },
    setGlobalAdSetIds(state, { payload }: PayloadAction<string[]>) {
      state.globalAdSetIds = payload
    },
    setSelectedAdSetIds(state, { payload }: PayloadAction<string[]>) {
      state.selectedAdSetIds = payload
      // Clean up overrides + cached metadata for removed destinations
      const keep = new Set(payload)
      for (const id of Object.keys(state.destinationOverrides)) {
        if (!keep.has(id)) delete state.destinationOverrides[id]
      }
      for (const id of Object.keys(state.selectedAdSetsData)) {
        if (!keep.has(id)) delete state.selectedAdSetsData[id]
      }
    },
    addSelectedAdSetId(state, { payload }: PayloadAction<string>) {
      if (!state.selectedAdSetIds.includes(payload)) {
        state.selectedAdSetIds.push(payload)
      }
    },
    /**
     * Preferred action — carries full AdSet metadata alongside the id so the
     * builder can resolve it later even when search/filter state changes.
     */
    addSelectedAdSet(state, { payload }: PayloadAction<AdSet>) {
      const id = String(payload.id)
      if (!state.selectedAdSetIds.includes(id)) {
        state.selectedAdSetIds.push(id)
      }
      state.selectedAdSetsData[id] = payload
    },
    removeSelectedAdSetId(state, { payload }: PayloadAction<string>) {
      state.selectedAdSetIds = state.selectedAdSetIds.filter(
        id => id !== payload,
      )
      delete state.destinationOverrides[payload]
      delete state.selectedAdSetsData[payload]
    },
    clearSelectedAdSetIds(state) {
      state.selectedAdSetIds = []
      state.destinationOverrides = {}
      state.selectedAdSetsData = {}
    },
    /**
     * Merge/update cached AdSet metadata without changing selection. Useful
     * when an id is already selected and a fresh search result arrives — we
     * still want the cached entry to reflect the latest values.
     */
    upsertSelectedAdSetData(state, { payload }: PayloadAction<AdSet[]>) {
      for (const adSet of payload) {
        const id = String(adSet.id)
        if (state.selectedAdSetIds.includes(id)) {
          state.selectedAdSetsData[id] = adSet
        }
      }
    },
    setDestinationOverride(
      state,
      {
        payload,
      }: PayloadAction<{ adSetId: string; override: DestinationOverride }>,
    ) {
      const cleaned: DestinationOverride = {}
      for (const [k, v] of Object.entries(payload.override)) {
        if (v === undefined) continue
        if (Array.isArray(v) && v.length === 0) continue
        if (
          typeof v === "object" &&
          v !== null &&
          !Array.isArray(v) &&
          Object.keys(v).length === 0
        ) {
          continue
        }
        const target = cleaned as Record<string, unknown>
        target[k] = v
      }
      if (Object.keys(cleaned).length === 0) {
        delete state.destinationOverrides[payload.adSetId]
      } else {
        state.destinationOverrides[payload.adSetId] = cleaned
      }
    },
    clearDestinationOverride(state, { payload }: PayloadAction<string>) {
      delete state.destinationOverrides[payload]
    },
    setPhase(state, { payload }: PayloadAction<"build" | "review">) {
      state.phase = payload
    },
    setPrefillCreative(
      state,
      { payload }: PayloadAction<PrefillCreative | undefined>,
    ) {
      state.prefillCreative = payload
    },
    setSourceAd(state, { payload }: PayloadAction<SourceAd | undefined>) {
      state.sourceAd = payload
    },
    setProductContext(
      state,
      { payload }: PayloadAction<ProductContext | undefined>,
    ) {
      state.productContext = payload
    },
    setPendingAiAction(
      state,
      { payload }: PayloadAction<AdForgeState["pendingAiAction"]>,
    ) {
      state.pendingAiAction = payload
    },
    resetAdForge() {
      return initialState
    },
  },
})

export const adForgeActions = adForgeSlice.actions
export default adForgeSlice.reducer
